import {useEffect, useState} from 'react';
import {
  ProfileApiHomeHeaderAppearance,
  ProfileApiHomeHeaderTheme,
  profileApi,
} from '../../../../services/profileApi';
import {HOME_HEADER_THEMES} from '../constants';

type ImagePosition = {
  x: number;
  y: number;
};

type NormalizedHomeHeaderAppearance = {
  theme: ProfileApiHomeHeaderTheme;
  customImageUrl: string;
  imagePosition: ImagePosition;
  imageZoom: number;
};

const DEFAULT_HOME_HEADER_APPEARANCE: NormalizedHomeHeaderAppearance = {
  theme: 'purple',
  customImageUrl: '',
  imagePosition: {x: 50, y: 50},
  imageZoom: 1,
};

const normalizeHomeHeaderAppearance = (
  appearance?: ProfileApiHomeHeaderAppearance
): NormalizedHomeHeaderAppearance => ({
  theme: appearance?.theme || DEFAULT_HOME_HEADER_APPEARANCE.theme,
  customImageUrl: appearance?.customImageUrl || '',
  imagePosition: {
    x: typeof appearance?.imagePosition?.x === 'number'
      ? appearance.imagePosition.x
      : DEFAULT_HOME_HEADER_APPEARANCE.imagePosition.x,
    y: typeof appearance?.imagePosition?.y === 'number'
      ? appearance.imagePosition.y
      : DEFAULT_HOME_HEADER_APPEARANCE.imagePosition.y,
  },
  imageZoom: typeof appearance?.imageZoom === 'number'
    ? appearance.imageZoom
    : DEFAULT_HOME_HEADER_APPEARANCE.imageZoom,
});

export const useHomeHeaderTheme = (token: string | null) => {
  const [appearance, setAppearance] = useState<NormalizedHomeHeaderAppearance>(DEFAULT_HOME_HEADER_APPEARANCE);

  useEffect(() => {
    const handleThemeChange = (event: Event) => {
      const customEvent = event as CustomEvent<ProfileApiHomeHeaderAppearance>;
      setAppearance(normalizeHomeHeaderAppearance(customEvent.detail));
    };

    window.addEventListener('headerThemeChange', handleThemeChange as EventListener);
    return () => window.removeEventListener('headerThemeChange', handleThemeChange as EventListener);
  }, []);

  useEffect(() => {
    if (!token) {
      setAppearance(DEFAULT_HOME_HEADER_APPEARANCE);
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const loadAppearance = async () => {
      try {
        const nextAppearance = await profileApi.getMyHomeHeaderAppearance(token, controller.signal);

        if (!isMounted) {
          return;
        }

        setAppearance(normalizeHomeHeaderAppearance(nextAppearance));
      } catch (error) {
        if (!isMounted || controller.signal.aborted) {
          return;
        }

        setAppearance(DEFAULT_HOME_HEADER_APPEARANCE);
      }
    };

    void loadAppearance();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [token]);

  const fallbackTheme = appearance.theme === 'custom'
    ? DEFAULT_HOME_HEADER_APPEARANCE.theme
    : appearance.theme;

  const backgroundStyle = {
    backgroundImage: appearance.customImageUrl
      ? `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${appearance.customImageUrl})`
      : HOME_HEADER_THEMES[fallbackTheme] || HOME_HEADER_THEMES.purple,
    backgroundSize: appearance.customImageUrl ? `${appearance.imageZoom * 100}%` : 'cover',
    backgroundPosition: appearance.customImageUrl
      ? `${appearance.imagePosition.x}% ${appearance.imagePosition.y}%`
      : 'center',
    backgroundRepeat: 'no-repeat',
  };

  return {
    headerTheme: appearance.theme,
    customHeaderImage: appearance.customImageUrl,
    imagePosition: appearance.imagePosition,
    imageZoom: appearance.imageZoom,
    backgroundStyle,
  };
};
